const MAX_SELECTED_EVENTS = 10;
const MAX_EVENT_TEXT = 500;
const MAX_GROUPS = 40;

function clip(text, max) {
    const value = String(text ?? "");
    return value.length > max ? value.slice(0, max) + "…" : value;
}

/**
 * Build the read-only project summary sent with each Ask message
 * (notes/ai-assistant-phase1.md §3.4). Pure so it can be unit tested; the
 * store-backed wrapper lives in assistantStore.svelte.js.
 *
 * @param {object} params
 * @param {object} params.project        projectState
 * @param {Array} params.groups          eventGroupState
 * @param {number} params.playerTime     playerState.time (seconds)
 * @param {object} [params.auth]         authState
 * @param {(index: number) => boolean} params.isLocked
 * @param {(seconds: number) => string} params.formatTc   seconds (without incode) to timecode
 * @param {(html: string) => string} params.toPlainText
 * @param {string} [params.uiLanguage]  app interface locale, e.g. "en"
 */
export function buildProjectContext({ project, groups, playerTime, auth, isLocked, formatTc, toPlainText, uiLanguage }) {
    const groupList = Array.isArray(groups) ? groups : [];
    const selectedGroupIndex = Number.isInteger(project?.selected) && groupList[project.selected]
        ? project.selected
        : null;

    const summary = {
        project: {
            name: clip(project?.name ?? "", 200),
            frameRate: project?.frameRate ?? null,
            dropFrame: Boolean(project?.dropFrame),
            incode: formatTc(0),
            mediaType: project?.media?.type ?? null,
            duration: project?.media?.duration ? formatTc(project.media.duration) : null,
        },
        playerTime: formatTc(playerTime || 0),
        plan: auth?.plan ? `${auth.plan}${auth.status === "in_trial" ? " (trial)" : ""}` : null,
        uiLanguage: typeof uiLanguage === "string" && uiLanguage ? clip(uiLanguage, 20) : null,
        groups: groupList.slice(0, MAX_GROUPS).map((group, index) => ({
            name: clip(group?.name ?? "", 120),
            type: group?.type ?? null,
            language: group?.language ?? null,
            eventCount: Array.isArray(group?.events) ? group.events.length : 0,
            locked: Boolean(isLocked(index)),
            guidelines: {
                maxCps: group?.maxCps,
                maxWpm: group?.maxWpm,
                maxChars: group?.maxChars,
                maxLines: group?.maxLines,
                minDuration: group?.minDuration,
                maxDuration: group?.maxDuration,
            },
        })),
        selectedGroupIndex,
        selectedEvents: [],
    };

    if (selectedGroupIndex !== null) {
        const group = groupList[selectedGroupIndex];
        const selected = Array.isArray(group.selected) ? group.selected : [];
        summary.selectedEvents = selected
            .slice(0, MAX_SELECTED_EVENTS)
            .filter((eventIndex) => group.events?.[eventIndex])
            .map((eventIndex) => {
                const event = group.events[eventIndex];
                return {
                    index: eventIndex,
                    start: Number.isFinite(event.start) ? formatTc(event.start) : null,
                    end: Number.isFinite(event.end) ? formatTc(event.end) : null,
                    text: clip(toPlainText(event.text ?? ""), MAX_EVENT_TEXT),
                };
            });
    }

    return summary;
}
